import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { Building2, Rocket, Cpu, Globe } from "lucide-react";

export const SocialProofSection = () => {
  const { t } = useLanguage();

  const stats = [
    {
      icon: Building2,
      value: "100+",
      label: t("socialProof.stat1"),
      iconColor: "text-blue-500",
      iconBg: "bg-blue-500/10",
      hoverIconBg: "group-hover:bg-blue-500/20"
    },
    {
      icon: Rocket,
      value: "3",
      unit: t("socialProof.stat2Unit"),
      label: t("socialProof.stat2"),
      iconColor: "text-amber-500",
      iconBg: "bg-amber-500/10",
      hoverIconBg: "group-hover:bg-amber-500/20"
    },
    {
      icon: Cpu,
      value: "AI",
      label: t("socialProof.stat3"),
      iconColor: "text-violet-500",
      iconBg: "bg-violet-500/10",
      hoverIconBg: "group-hover:bg-violet-500/20"
    },
    {
      icon: Globe,
      value: "SEA",
      label: t("socialProof.stat4"),
      iconColor: "text-teal-500",
      iconBg: "bg-teal-500/10",
      hoverIconBg: "group-hover:bg-teal-500/20"
    }
  ];

  const testimonials = [
    {
      quote: t("socialProof.testimonial1.quote"),
      avatar: t("socialProof.testimonial1.avatar"),
      name: t("socialProof.testimonial1.name"),
      company: t("socialProof.testimonial1.company")
    },
    {
      quote: t("socialProof.testimonial2.quote"),
      avatar: t("socialProof.testimonial2.avatar"),
      name: t("socialProof.testimonial2.name"),
      company: t("socialProof.testimonial2.company")
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-background to-secondary/30 relative overflow-hidden">
      {/* Grid pattern background */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(255,255,255,0.2) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255,255,255,0.2) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px'
        }}
      />
      
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-primary/5 rounded-full blur-2xl" />
      </div>

      <div className="container max-w-7xl mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
            {t("socialProof.title")}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t("socialProof.desc")}
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="group relative"
            >
              <div className="relative h-full p-5 md:p-6 rounded-2xl bg-white/40 dark:bg-white/5 backdrop-blur-xl border border-white/50 dark:border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.04)] hover:bg-white/60 dark:hover:bg-white/10 hover:border-secondary/30 hover:shadow-[0_12px_40px_rgba(0,0,0,0.08)] transition-all duration-500 text-center">
                {/* Icon */}
                <div className={`w-12 h-12 md:w-14 md:h-14 mx-auto mb-4 bg-white/60 dark:bg-white/10 backdrop-blur-xs rounded-xl flex items-center justify-center border border-white/40 group-hover:bg-gradient-ai group-hover:border-transparent group-hover:shadow-ai transition-all duration-300`}>
                  <stat.icon className={`w-6 h-6 md:w-7 md:h-7 ${stat.iconColor} group-hover:text-primary-foreground transition-colors`} />
                </div>
                
                {/* Value */}
                <div className="flex items-baseline justify-center gap-1 mb-1">
                  <span className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground group-hover:text-secondary transition-colors duration-300">
                    {stat.value}
                  </span>
                  {stat.unit && (
                    <span className="text-sm md:text-base text-muted-foreground">
                      {stat.unit}
                    </span>
                  )}
                </div>
                
                {/* Label */}
                <p className="text-sm md:text-base text-muted-foreground">
                  {stat.label}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 items-stretch">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
              className="group relative"
            >
              <div className="relative h-full p-6 md:p-8 rounded-2xl bg-white/40 dark:bg-white/5 backdrop-blur-xl border border-white/50 dark:border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.04)] hover:bg-white/60 dark:hover:bg-white/10 hover:border-secondary/30 hover:shadow-[0_12px_40px_rgba(0,0,0,0.08)] transition-all duration-500">
                {/* Quote icon - stylized double quote */}
                <div className="mb-4">
                  <svg width="32" height="28" viewBox="0 0 32 28" fill="none" className="text-secondary">
                    <path d="M0 16.8C0 10.08 4.32 4.32 12 0L13.68 3.12C9.36 6.24 7.2 9.84 7.2 14.16C7.2 14.64 7.2 15.12 7.2 15.6H12V28H0V16.8ZM18 16.8C18 10.08 22.32 4.32 30 0L31.68 3.12C27.36 6.24 25.2 9.84 25.2 14.16C25.2 14.64 25.2 15.12 25.2 15.6H30V28H18V16.8Z" fill="currentColor" fillOpacity="0.6"/>
                  </svg>
                </div>
                
                {/* Quote text */}
                <p className="text-foreground/90 text-base md:text-lg leading-relaxed mb-8">
                  "{testimonial.quote}"
                </p>
                
                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-secondary-foreground font-semibold text-sm shadow-md">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-semibold text-foreground text-sm">{testimonial.name}</div>
                    <div className="text-muted-foreground text-xs">{testimonial.company}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
